
import { mdiVolumeHigh, mdiVolumeOff } from "@mdi/js"
import themes from "@/mixins/themes"
import intersectionObserver from "@/mixins/intersectionObserver"
import media from "@/mixins/media"
import wheel from "@/directives/wheelDirective"
import "swiper/swiper.scss"
import Swiper from "swiper"

export default {
    name: "JobSlideShowV3",
    components: {
        Button: () => import("@/components/base/Button"),
        OPicture: () => import("@/components/base/image/OPicture"),
        ResponsiveVimeo: () =>
            import("@/components/base/video/ResponsiveVimeo"),
    },
    directives: { wheel },
    mixins: [themes, intersectionObserver, media],
    emits: ["show-more"],
    props: {
        /** Array of images/videos */
        mediaList: {
            type: Array,
            default: null,
        },
        /** Mutes video, disables sound switch button and tooltip */
        muted: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        swiper: null,
        videoMuted: true,
        audioStatuses: [],
        showTooltip: false,
    }),
    icons: {
        mdiVolumeHigh,
        mdiVolumeOff,
    },
    computed: {
        hasDisplayableVideos() {
            return this.mediaList.some((media) =>
                this.isVideoDisplayable(media)
            )
        },
        currentSlide() {
            return this.swiper?.realIndex ?? 0
        },
    },
    mounted() {
        // if (process.server) return
        this.swiper = new Swiper(this.$refs.jobSlideShowSwiper, {
            slidesPerView: "auto",
            spaceBetween: 0,
            initialSlide: 0,
            resistanceRatio: 0.85,
            longSwipesRatio: 1,
            allowTouchMove: true,
            grabCursor: true,
        })
        // Updates dynamic widths of slides and sets up the correct position
        setTimeout(() => {
            this.swiper.updateSlides()
            this.swiper.slideTo(0)
            this.swiper.on("slideChange", () => {
                this.showTooltip = false
            })
        }, 500)
    },
    methods: {
        getComponentProps(media, index) {
            if (this.isVideo(media))
                return {
                    ...this.$attrs,
                    forcePlaceholder: this.$speed.isSlow !== false,
                    video: media,
                    muted:
                        this.muted ||
                        this.videoMuted ||
                        this.currentSlide !== index,
                    autoplay: true,
                    loop: true,
                    preload: "none",
                    playsinline: true,
                }
            return {
                ...this.$attrs,
                image: media,
                alt: media.file_name ?? "Beschreibungsbild",
                size: "2",
                height: 350,
                fit: "cover",
                loading: "lazy",
            }
        },
        onVideoReady({ hasAudio }, index) {
            this.audioStatuses.push({
                id: index,
                hasAudio: hasAudio,
            })
            const videoRef = this.$refs[`job-slide-show-media-${index}`][0]
            this.observeIntersection(
                videoRef?.$el,
                () => {
                    videoRef.playVideo()
                },
                () => {
                    videoRef.pauseVideo()
                },
                {
                    threshold: 0.4,
                }
            )
        },
        onSwitchSound() {
            this.videoMuted = !this.videoMuted
            if (this.videoMuted) this.showTooltip = false
            else {
                const currentStatus = this.audioStatuses.findIndex(
                    (status) =>
                        status.id === this.currentSlide && !status.hasAudio
                )
                if (currentStatus !== -1) {
                    this.showTooltip = true
                    setTimeout(() => (this.showTooltip = false), 3000)
                }
            }
        },
        onWheel(e) {
            // Surpassing the inertia of the mouse wheel/trackpad
            const dx = Math.abs(e.deltaX),
                dy = Math.abs(e.deltaY)
            const scrollingCondition =
                dx >= this.prevDeltaX || dy >= this.prevDeltaY
            if (scrollingCondition && !this.isScrolling) {
                this.isScrolling = true
                this.prevDeltaX = dx
                this.prevDeltaY = dy
                if (e.deltaX > 0) this.swiper.slideNext()
                else if (e.deltaX < 0) this.swiper.slidePrev()
            } else {
                clearTimeout(this.scrollingTimeout)
            }

            this.scrollingTimeout = setTimeout(() => {
                this.isScrolling = false
                this.prevDeltaX = 0
                this.prevDeltaY = 0
            }, 30)
        },
    },
}
